import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import type { FunctionComponent, ReactElement } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';

import type { GlobalSearchJobLevel, GlobalSearchJobRole } from '../../../../data-contract/global-search';
import { TableLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { pageSizeOptions } from '../../../common/data-grids/common/constants';
import { GeneralDataGrid } from '../../../common/data-grids/common/GeneralDataGrid';
import { TableCellLinkWrapper } from '../../../common/data-grids/common/TableCellLinkWrapper';
import { useTablePagination } from '../../../common/data-grids/common/use-table-pagination';
import {
    buildJobLevelSearchResultUrl,
    buildJobRoleSearchResultUrl,
} from '../../../global-search/job-architecture-result-url';
import { defaultPagination } from '../../../global-search/use-global-search-table-params';
import { useGlobalSearchJobLevels } from '../../../query-hooks/global-search/job-levels';
import { useGlobalSearchJobRoles } from '../../../query-hooks/global-search/job-roles';

type JobArchitectureResultsDataGridProps<T extends { readonly id: string }> = {
    readonly columns: readonly GridColDef<T>[];
    readonly isFetching: boolean;
    readonly isPending: boolean;
    readonly noDataMessage: string;
    readonly onPaginationModelChange: (model: GridPaginationModel) => void;
    readonly onRowClick: (row: T) => void;
    readonly paginationModel: GridPaginationModel;
    readonly rowCount: number;
    readonly rows: readonly T[];
};

const JobArchitectureResultsDataGrid = <T extends { readonly id: string }>({
    columns,
    isFetching,
    isPending,
    noDataMessage,
    onPaginationModelChange,
    onRowClick,
    paginationModel,
    rowCount,
    rows,
}: JobArchitectureResultsDataGridProps<T>): ReactElement => {
    return (
        <GeneralDataGrid
            columns={columns}
            disableColumnMenu={true}
            disableMultipleColumnsSorting={true}
            disableRowSelectionOnClick={true}
            getRowHeight={() => 'auto'}
            loading={isPending || isFetching}
            localeText={{ noRowsLabel: noDataMessage }}
            pageSizeOptions={pageSizeOptions}
            pagination={true}
            paginationMode="server"
            paginationModel={paginationModel}
            rowCount={rowCount}
            rows={rows}
            slots={{ loadingOverlay: TableLoadingSpinner }}
            sx={{
                '& .MuiDataGrid-cell': {
                    alignItems: 'center',
                    py: '8px',
                },
                '& .MuiDataGrid-cell .MuiDataGrid-cellContent': {
                    display: 'block',
                    minHeight: 'auto',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
                '& .MuiDataGrid-cell .MuiGrid-root': {
                    minWidth: 0,
                    minHeight: 'auto',
                    overflow: 'hidden',
                },
                '& .MuiDataGrid-cell .MuiLink-root': {
                    display: 'block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
                height: '70vh',
            }}
            onPaginationModelChange={onPaginationModelChange}
            onRowClick={({ row }) => onRowClick(row)}
        />
    );
};

type SearchTermTableProps = {
    readonly searchTerm: string;
};

const renderEllipsisCell = (value: string | null | undefined): ReactElement => {
    const label = value ?? '';

    return (
        <span
            title={label}
            style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </span>
    );
};

export const JobRolesSearchTermTable: FunctionComponent<SearchTermTableProps> = ({ searchTerm }) => {
    const { actions, translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();

    const { data, isFetching, isPending } = useGlobalSearchJobRoles({
        page: paginationModel.page,
        searchTerm,
        size: paginationModel.pageSize,
    });

    const rows = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;
    const noDataMessage = translate('pages.global-search.no-data.job-roles');

    const columns: readonly GridColDef<GlobalSearchJobRole>[] = [
        {
            field: 'title',
            headerName: translate('pages.global-search.headlines.job-role'),
            renderCell: ({ row }) => (
                <TableCellLinkWrapper to={buildJobRoleSearchResultUrl(row)}>{row.title}</TableCellLinkWrapper>
            ),
            sortable: false,
            type: 'string',
            width: 320,
        },
        {
            field: 'jobArchitectureTitle',
            headerName: translate('pages.global-search.headlines.job-architecture'),
            sortable: false,
            type: 'string',
            width: 260,
        },
        {
            field: 'familyTitle',
            headerName: translate('pages.global-search.headlines.job-family'),
            sortable: false,
            type: 'string',
            width: 220,
        },
        {
            field: 'clusterTitle',
            headerName: translate('pages.global-search.headlines.job-cluster'),
            renderCell: ({ row }) => renderEllipsisCell(row.clusterTitle),
            sortable: false,
            type: 'string',
            width: 320,
        },
    ];

    return (
        <JobArchitectureResultsDataGrid
            columns={columns}
            isFetching={isFetching}
            isPending={isPending}
            noDataMessage={noDataMessage}
            paginationModel={paginationModel}
            rowCount={pagination.count}
            rows={rows}
            onPaginationModelChange={setPaginationModel}
            onRowClick={(row) => actions.navigateTo(buildJobRoleSearchResultUrl(row))}
        />
    );
};

export const JobLevelsSearchTermTable: FunctionComponent<SearchTermTableProps> = ({ searchTerm }) => {
    const { actions, translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();

    const { data, isFetching, isPending } = useGlobalSearchJobLevels({
        page: paginationModel.page,
        searchTerm,
        size: paginationModel.pageSize,
    });

    const rows = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;
    const noDataMessage = translate('pages.global-search.no-data.job-levels');

    const columns: readonly GridColDef<GlobalSearchJobLevel>[] = [
        {
            field: 'title',
            headerName: translate('pages.global-search.headlines.job-level'),
            renderCell: ({ row }) => (
                <TableCellLinkWrapper to={buildJobLevelSearchResultUrl(row)}>{row.title}</TableCellLinkWrapper>
            ),
            sortable: false,
            type: 'string',
            width: 260,
        },
        {
            field: 'roleTitle',
            headerName: translate('pages.global-search.headlines.job-role'),
            sortable: false,
            type: 'string',
            width: 260,
        },
        {
            field: 'jobArchitectureTitle',
            headerName: translate('pages.global-search.headlines.job-architecture'),
            sortable: false,
            type: 'string',
            width: 260,
        },
        {
            field: 'familyTitle',
            headerName: translate('pages.global-search.headlines.job-family'),
            sortable: false,
            type: 'string',
            width: 220,
        },
        {
            field: 'clusterTitle',
            headerName: translate('pages.global-search.headlines.job-cluster'),
            renderCell: ({ row }) => renderEllipsisCell(row.clusterTitle),
            sortable: false,
            type: 'string',
            width: 220,
        },
    ];

    return (
        <JobArchitectureResultsDataGrid
            columns={columns}
            isFetching={isFetching}
            isPending={isPending}
            noDataMessage={noDataMessage}
            paginationModel={paginationModel}
            rowCount={pagination.count}
            rows={rows}
            onPaginationModelChange={setPaginationModel}
            onRowClick={(row) => actions.navigateTo(buildJobLevelSearchResultUrl(row))}
        />
    );
};
